import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  Bus,
  GraduationCap,
  Hospital,
  Landmark,
  Loader2,
  Map,
  MapPin,
  Plane,
  Route,
  School,
  Settings2,
  ShoppingBag,
  TrainFront,
  Trees,
} from "lucide-react";
import PropertyMap from "@/components/property/PropertyMap";
import { Button } from "@/components/ui/button";
import { api, asItems, formatApiError } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export const INVENTORY_LAYERS = [
  { id: "FOR_SALE", label: "For Sale" },
  { id: "NEW", label: "New" },
  { id: "SOLD", label: "Sold" },
  { id: "PROJECTS", label: "Projects" },
];

export const CONTEXT_OVERLAYS = [
  { id: "SCHOOL", label: "Schools", icon: School },
  { id: "HOSPITAL", label: "Hospitals", icon: Hospital },
  { id: "METRO", label: "Metro", icon: TrainFront },
  { id: "RAILWAY", label: "Railway", icon: TrainFront },
  { id: "TRANSIT", label: "Transit", icon: Bus },
  { id: "AIRPORT", label: "Airport", icon: Plane },
  { id: "SHOPPING", label: "Shopping", icon: ShoppingBag },
  { id: "PARK", label: "Parks", icon: Trees },
  { id: "LANDMARK", label: "Landmarks", icon: Landmark },
  { id: "HIGHWAY", label: "Highways", icon: Route },
  { id: "UNIVERSITY", label: "Universities", icon: GraduationCap },
  { id: "BUSINESS_HUB", label: "Business hubs", icon: Building2 },
  { id: "INDUSTRIAL_AREA", label: "Industrial areas", icon: Building2 },
  { id: "RESIDENTIAL_PROJECT", label: "Major projects", icon: Building2 },
  { id: "ESSENTIALS", label: "Essentials", icon: ShoppingBag },
];

const DISTANCE_OPTIONS = [1000, 3000, 5000, 10000];
const PROPERTY_TYPE_OPTIONS = [
  { id: "PLOTS", label: "Plots", categories: ["LAND", "PLOT"] },
  { id: "VILLAS", label: "Villas", categories: ["VILLA"] },
  { id: "APARTMENTS", label: "Apartments", categories: ["FLAT", "APARTMENT"] },
  { id: "HOUSES", label: "Houses", categories: ["HOUSE"] },
  { id: "COMMERCIAL", label: "Commercial", categories: ["COMMERCIAL"] },
];
const CONNECTIVITY_CATEGORIES = new Set(["METRO", "RAILWAY", "BUS_STOP", "AIRPORT", "HIGHWAY", "MAJOR_ROAD", "BUSINESS_HUB", "UNIVERSITY", "RESIDENTIAL_PROJECT"]);

function propertyId(property) {
  return String(property?.property_id || property?.id || "");
}

function isNew(property) {
  const value = property?.published_at || property?.listed_at || property?.created_at;
  const publishedAt = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(publishedAt) && Date.now() - publishedAt <= 14 * 24 * 60 * 60 * 1000;
}

export function propertiesForLayer(properties, layer) {
  if (layer === "NEW") return properties.filter(isNew);
  if (layer === "PROJECTS") {
    return properties.filter((property) => ["PROJECT", "PROJECTS"].includes(String(property.category || property.property_type || "").toUpperCase()));
  }
  if (layer === "SOLD") {
    return properties.filter((property) => property.layer === "SOLD" && property.verified === true);
  }
  return properties.filter((property) => String(property.status || "PUBLISHED").toUpperCase() === "PUBLISHED");
}

function toMarker(property, isCurrent = false) {
  const id = propertyId(property);
  return {
    ...property,
    id,
    isCurrent,
    href: id ? `/properties/${id}` : undefined,
    statusLabel: isCurrent ? undefined : property.layer === "SOLD" ? "SOLD" : isNew(property) ? "NEW" : undefined,
  };
}

function propertyTypeGroup(property) {
  const category = String(property?.category || property?.property_type || "").toUpperCase();
  return PROPERTY_TYPE_OPTIONS.find((option) => option.categories.includes(category))?.id || null;
}

export default function PropertyContextMap({ property, nearbyProperties = [], compact = false }) {
  const currentId = propertyId(property);
  const [inventoryLayer, setInventoryLayer] = useState("FOR_SALE");
  const [contextOverlay, setContextOverlay] = useState(null);
  const [mapStyle, setMapStyle] = useState("map");
  const [selectedMarkerId, setSelectedMarkerId] = useState(currentId);
  const [radiusMeters, setRadiusMeters] = useState(5000);
  const [nearbyResults, setNearbyResults] = useState({});
  const [soldResults, setSoldResults] = useState({});
  const [propertyTypes, setPropertyTypes] = useState(() => {
    const currentType = propertyTypeGroup(property);
    return currentType ? [currentType] : [];
  });
  const requestedNearbyKeys = useRef(new Set());
  const requestedSoldKeys = useRef(new Set());
  const requestedContextKeys = useRef(new Set());
  const [contextResults, setContextResults] = useState({});
  const mounted = useRef(true);
  const nearbyKey = contextOverlay && currentId ? `${currentId}:${contextOverlay}:${radiusMeters}` : null;
  const activeNearby = nearbyKey ? nearbyResults[nearbyKey] : null;
  const soldKey = inventoryLayer === "SOLD" && currentId ? `${currentId}:SOLD:${radiusMeters}` : null;
  const activeSold = soldKey ? soldResults[soldKey] : null;
  // V3 §76: radius-based inventory pins. The passed nearby list stays as the
  // no-fetch fallback so the map renders immediately from core page data.
  const contextKey = inventoryLayer !== "SOLD" && currentId && (nearbyProperties.length === 0 || radiusMeters !== 5000 || inventoryLayer !== "FOR_SALE")
    ? `${currentId}:${inventoryLayer}:${radiusMeters}` : null;
  const activeContextInventory = contextKey ? contextResults[contextKey] : null;

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!nearbyKey || requestedNearbyKeys.current.has(nearbyKey)) return undefined;
    requestedNearbyKeys.current.add(nearbyKey);
    setNearbyResults((current) => ({
      ...current,
      [nearbyKey]: { loading: true, items: [], error: "" },
    }));
    api.get(`/web/property/${encodeURIComponent(currentId)}/nearby`, {
      params: { category: contextOverlay, radius: radiusMeters, limit: 20 },
    }).then(({ data }) => {
      if (!mounted.current) return;
      setNearbyResults((current) => ({
        ...current,
        [nearbyKey]: { ...data, items: asItems(data), loading: false, error: "" },
      }));
    }).catch((requestError) => {
      requestedNearbyKeys.current.delete(nearbyKey);
      if (!mounted.current) return;
      setNearbyResults((current) => ({
        ...current,
        [nearbyKey]: {
          loading: false,
          items: [],
          error: formatApiError(requestError) || "Nearby places could not be loaded.",
        },
      }));
    });
    return undefined;
  }, [contextOverlay, currentId, nearbyKey, radiusMeters]);

  useEffect(() => {
    if (!soldKey || requestedSoldKeys.current.has(soldKey)) return undefined;
    requestedSoldKeys.current.add(soldKey);
    setSoldResults((current) => ({
      ...current,
      [soldKey]: { loading: true, items: [], error: "" },
    }));
    api.get(`/web/property/${encodeURIComponent(currentId)}/comparables`, {
      params: { type: "sold", radius: radiusMeters, limit: 20 },
    }).then(({ data }) => {
      if (!mounted.current) return;
      setSoldResults((current) => ({
        ...current,
        [soldKey]: { ...data, items: asItems(data), loading: false, error: "" },
      }));
    }).catch((requestError) => {
      requestedSoldKeys.current.delete(soldKey);
      if (!mounted.current) return;
      setSoldResults((current) => ({
        ...current,
        [soldKey]: {
          loading: false,
          items: [],
          error: formatApiError(requestError) || "Verified sold properties could not be loaded.",
        },
      }));
    });
    return undefined;
  }, [currentId, radiusMeters, soldKey]);

  useEffect(() => {
    if (!contextKey || requestedContextKeys.current.has(contextKey)) return undefined;
    requestedContextKeys.current.add(contextKey);
    setContextResults((current) => ({ ...current, [contextKey]: { loading: true, items: [], error: "" } }));
    api.get(`/web/property/${encodeURIComponent(currentId)}/map-context`, {
      params: { layer: inventoryLayer, radius: radiusMeters, limit: 50 },
    }).then(({ data }) => {
      if (!mounted.current) return;
      setContextResults((current) => ({
        ...current,
        [contextKey]: { loading: false, items: data?.pins || asItems(data) || [], error: "" },
      }));
    }).catch((requestError) => {
      requestedContextKeys.current.delete(contextKey);
      if (!mounted.current) return;
      setContextResults((current) => ({
        ...current,
        [contextKey]: { loading: false, items: [], error: formatApiError(requestError) || "Nearby property pins could not be loaded." },
      }));
    });
    return undefined;
  }, [contextKey, currentId, inventoryLayer, radiusMeters]);

  const inventoryProperties = useMemo(() => {
    const source = inventoryLayer === "SOLD"
      ? (activeSold?.items || [])
      : (activeContextInventory && !activeContextInventory.loading && !activeContextInventory.error && activeContextInventory.items.length
          ? activeContextInventory.items
          : nearbyProperties);
    const nearby = source.filter((item) => propertyId(item) !== currentId);
    const typed = propertyTypes.length
      ? nearby.filter((item) => propertyTypes.includes(propertyTypeGroup(item)))
      : nearby;
    return propertiesForLayer(typed, inventoryLayer);
  }, [activeSold, currentId, inventoryLayer, nearbyProperties, propertyTypes]);
  const poiMarkers = useMemo(
    () => (activeNearby?.items || []).map((place, index) => ({
      ...place,
      id: `poi-${place.category}-${place.lat}-${place.lng}-${index}`,
      kind: "poi",
      title: place.name,
    })),
    [activeNearby]
  );
  const markers = useMemo(
    () => [toMarker(property, true), ...inventoryProperties.map((item) => toMarker(item)), ...poiMarkers],
    [inventoryProperties, poiMarkers, property]
  );
  const activeContext = CONTEXT_OVERLAYS.find((item) => item.id === contextOverlay);
  const inventoryLoading = (inventoryLayer === "SOLD" && activeSold?.loading) || activeContextInventory?.loading;
  const inventoryError = inventoryLayer === "SOLD" ? activeSold?.error : (activeContextInventory?.error || "");
  const inventoryEmpty = inventoryLayer !== "FOR_SALE" && !inventoryLoading && !inventoryError
    && inventoryProperties.length === 0;

  const selectContext = (id) => {
    setContextOverlay((current) => current === id ? null : id);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-background" data-testid="property-context-map">
      <div className="border-b border-border bg-background p-3 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1 overflow-x-auto rounded-full border border-border bg-secondary/40 p-1 pb-1 sm:pb-1" role="group" aria-label="Property inventory layer">
            {INVENTORY_LAYERS.map((layer) => (
              <button
                key={layer.id}
                type="button"
                onClick={() => setInventoryLayer(layer.id)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${inventoryLayer === layer.id ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-primary"}`}
                aria-pressed={inventoryLayer === layer.id}
                data-testid={`inventory-${layer.id.toLowerCase()}`}
              >
                {layer.label}
              </button>
            ))}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden shrink-0 rounded-full border border-border p-0.5 sm:flex" role="group" aria-label="Map style">
              <button type="button" onClick={() => setMapStyle("map")} className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition ${mapStyle === "map" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-primary"}`} aria-pressed={mapStyle === "map"}><Map className="mr-1 inline h-3 w-3" />Map</button>
              <button type="button" onClick={() => setMapStyle("satellite")} className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition ${mapStyle === "satellite" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-primary"}`} aria-pressed={mapStyle === "satellite"}>Satellite</button>
            </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0 rounded-full" aria-label="Open map options" data-testid="map-options-trigger">
                <Settings2 className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent className="overflow-y-auto" data-testid="property-map-options">
              <SheetHeader>
                <SheetTitle className="font-display text-2xl">Map options</SheetTitle>
                <SheetDescription>Choose one property layer and an optional location context.</SheetDescription>
              </SheetHeader>
              <div className="mt-7 space-y-7">
                <fieldset>
                  <legend className="text-xs font-black uppercase tracking-widest text-muted-foreground">Property layer</legend>
                  <div className="mt-3 space-y-2">
                    {INVENTORY_LAYERS.map((layer) => (
                      <label key={layer.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-sm font-semibold">
                        <input type="radio" name="inventory-layer" value={layer.id} checked={inventoryLayer === layer.id} onChange={() => setInventoryLayer(layer.id)} />
                        {layer.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-xs font-black uppercase tracking-widest text-muted-foreground">Property type</legend>
                  <div className="mt-3 space-y-2">
                    {PROPERTY_TYPE_OPTIONS.map((option) => (
                      <label key={option.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-sm font-semibold">
                        <input type="checkbox" checked={propertyTypes.includes(option.id)} onChange={() => setPropertyTypes((current) => current.includes(option.id) ? current.filter((item) => item !== option.id) : [...current, option.id])} />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-xs font-black uppercase tracking-widest text-muted-foreground">Map view</legend>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {[{ id: "map", label: "Map" }, { id: "satellite", label: "Satellite" }].map((style) => (
                      <button key={style.id} type="button" onClick={() => setMapStyle(style.id)} className={`rounded-md border px-3 py-2 text-sm font-bold ${mapStyle === style.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} aria-pressed={mapStyle === style.id}>{style.label}</button>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-xs font-black uppercase tracking-widest text-muted-foreground">Distance</legend>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {DISTANCE_OPTIONS.map((distance) => (
                      <button key={distance} type="button" onClick={() => setRadiusMeters(distance)} className={`rounded-md border px-3 py-2 text-sm font-bold ${radiusMeters === distance ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} aria-pressed={radiusMeters === distance}>{distance / 1000} km</button>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-xs font-black uppercase tracking-widest text-muted-foreground">Location context</legend>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {CONTEXT_OVERLAYS.map((overlay) => (
                      <button key={overlay.id} type="button" onClick={() => selectContext(overlay.id)} className={`rounded-md border px-3 py-2 text-left text-xs font-bold ${contextOverlay === overlay.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} aria-pressed={contextOverlay === overlay.id}>{overlay.label}</button>
                    ))}
                  </div>
                </fieldset>
              </div>
            </SheetContent>
          </Sheet>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-muted-foreground">Nearby</span>
          <div className="flex min-w-0 gap-1.5 overflow-x-auto pb-0.5" aria-label="Location context overlay">
            {CONTEXT_OVERLAYS.slice(0, compact ? 4 : 10).map((overlay) => {
              const Icon = overlay.icon;
              return (
                <button
                  key={overlay.id}
                  type="button"
                  onClick={() => selectContext(overlay.id)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${contextOverlay === overlay.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-foreground/30 hover:text-primary"}`}
                  aria-pressed={contextOverlay === overlay.id}
                  data-testid={`context-${overlay.id.toLowerCase()}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {overlay.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <PropertyMap
        markers={markers}
        selectedMarkerId={selectedMarkerId}
        onMarkerSelect={setSelectedMarkerId}
        mapStyle={mapStyle}
        height={compact ? 320 : 460}
        className="rounded-none border-0"
        testId={compact ? "similar-properties-map" : "neighborhood-property-map"}
      />

      {(inventoryEmpty || inventoryLoading || inventoryError || activeContext) && (
        <div className="border-t border-border bg-secondary/30 px-4 py-3 text-xs text-muted-foreground" role="status" data-testid="map-layer-status">
          {inventoryLoading && <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />{inventoryLayer === "SOLD" ? "Loading verified sold properties..." : "Loading nearby property pins..."}</span>}
          {inventoryError && <span className="text-destructive">{inventoryError}</span>}
          {inventoryEmpty && inventoryLayer === "SOLD" && "Verified sold transactions will appear here when transaction data is available."}
          {inventoryEmpty && inventoryLayer === "NEW" && "No recently published nearby properties are available."}
          {inventoryEmpty && inventoryLayer === "PROJECTS" && "No mapped nearby projects are available."}
          {inventoryEmpty && activeContext && " "}
          {activeContext && activeNearby?.loading && <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading {activeContext.label.toLowerCase()}...</span>}
          {activeContext && activeNearby?.error && <span className="text-destructive">{activeNearby.error}</span>}
          {activeContext && !activeNearby?.loading && !activeNearby?.error && activeNearby?.message && activeNearby.message}
        </div>
      )}

      {activeContext && !activeNearby?.loading && !activeNearby?.error && (
        <NearbyPlacesPanel result={activeNearby} title={`${activeContext.label} nearby`} />
      )}

      <LoadedLocationSummary nearbyResults={nearbyResults} />
    </div>
  );
}

function NearbyPlacesPanel({ result, title }) {
  const places = result?.items || [];
  if (!places.length) return null;
  const connectivity = places.filter((place) => CONNECTIVITY_CATEGORIES.has(place.category));
  return (
    <div className="border-t border-border p-4 sm:p-5" data-testid="nearby-place-cards">
      <div className="flex items-end justify-between gap-3">
        <div><p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Location context</p><h4 className="mt-1 font-display text-lg font-bold text-primary">{title}</h4></div>
        <span className="text-[10px] font-semibold text-muted-foreground">Straight-line distance</span>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {places.slice(0, 8).map((place, index) => (
          <div key={`${place.category}-${place.name}-${index}`} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-secondary/10 p-3">
            <div className="min-w-0"><p className="truncate text-sm font-bold text-primary">{place.name}</p><p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{String(place.category).replaceAll("_", " ")}</p></div>
            <span className="shrink-0 text-xs font-black text-primary">{formatDistance(place.distance_meters)}</span>
          </div>
        ))}
      </div>
      {connectivity.length > 0 && (
        <div className="mt-5 rounded-xl border border-border bg-secondary/20 p-4" data-testid="land-connectivity-context">
          <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Connectivity & investment context</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">{connectivity.slice(0, 6).map((place, index) => <div key={`${place.name}-${index}`} className="flex justify-between gap-3 text-sm"><span className="truncate text-muted-foreground">{place.name}</span><span className="shrink-0 font-bold text-primary">{formatDistance(place.distance_meters)}</span></div>)}</div>
        </div>
      )}
      {result?.attribution && <p className="mt-3 text-[10px] text-muted-foreground">{result.attribution}</p>}
    </div>
  );
}

function LoadedLocationSummary({ nearbyResults }) {
  const places = Object.values(nearbyResults).flatMap((result) => result?.items || []);
  if (!places.length) return null;
  const byCategory = places.reduce((summary, place) => {
    const existing = summary[place.category];
    if (!existing || Number(place.distance_meters) < Number(existing.distance_meters)) summary[place.category] = place;
    return summary;
  }, {});
  return (
    <div className="border-t border-border bg-secondary/10 p-4 sm:p-5" data-testid="location-summary">
      <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><h4 className="font-display text-base font-bold text-primary">Location at a glance</h4></div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(byCategory).slice(0, 9).map(([category, place]) => (
          <div key={category} className="flex items-center justify-between gap-3 rounded-md bg-background px-3 py-2 text-xs"><span className="capitalize text-muted-foreground">{category.toLowerCase().replaceAll("_", " ")}</span><span className="font-bold text-primary">{formatDistance(place.distance_meters)}</span></div>
        ))}
      </div>
    </div>
  );
}

function formatDistance(value) {
  const distance = Number(value);
  if (!Number.isFinite(distance)) return "Distance unavailable";
  if (distance < 1000) return `${Math.round(distance)} m`;
  return `${(distance / 1000).toFixed(distance < 10000 ? 1 : 0)} km`;
}